import { watch } from "vue";
import type { ProgramSessionConfigurableFeesModel, SessionFeeSnapshot, UseSuppressedSessionFeesArgs } from "./types";
import { forceSessionFeesToZero, readSessionFeeSnapshot, restoreSessionFeesFromSnapshot } from "./utils";

/**
 * Outlives the component on purpose: ExpandingSection unmounts the fee block on collapse, and losing the
 * baseline there would make an accidental owner toggle unrecoverable. Keyed on the session object, so a
 * load or save starts a fresh baseline while a remount over the same session keeps the existing one.
 */
const baselines = new WeakMap<ProgramSessionConfigurableFeesModel, SessionFeeSnapshot>();

/**
 * `0` rather than `undefined` is deliberate: it stops `useConfigurableSessionPayments` re-applying its
 * new-session defaults, whose guard only fires when both fields are `undefined`.
 */
export const useSuppressedSessionFees = ({
  programSession,
  suppressed,
  selectedOwnerEntityId,
  loading,
}: UseSuppressedSessionFeesArgs): void => {
  watch(
    [suppressed, programSession, selectedOwnerEntityId, loading],
    ([isSuppressed, session, ownerEntityId, isLoading]) => {
      if (!baselines.has(session)) {
        baselines.set(session, readSessionFeeSnapshot(session, ownerEntityId));
      }

      // Neither force nor restore until the owner's settings resolve.
      if (isLoading) {
        return;
      }

      if (isSuppressed) {
        forceSessionFeesToZero(session);
      } else {
        restoreSessionFeesFromSnapshot(session, baselines.get(session), ownerEntityId);
      }
    },
    { immediate: true },
  );
};
